/**
 * voice-v8 — Öffnungszeiten lesen und beantworten: offen oder zu?
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Martin (2026-08-24): "Bitte schau mal nach, wann heute der Arzt in Hollenthon
 * wieder öffnet." — und später: "Bitte die Kontaktseite überarbeiten, damit sie
 * deutlich schöner wird, und erweitern auch mit den Zusatzinformationen wie
 * Öffnungszeiten."
 *
 *   Die Zeiten stehen längst in den Kontakten. Sichtbar war aber nur Name und
 *   Telefon; die Zeiten lagen zugeklappt darunter — und selbst aufgeklappt
 *   musste man sie im Kopf mit der Uhr vergleichen.
 *
 *   Die Frage ist nie "wie lauten die Öffnungszeiten". Sie lautet "kann ich
 *   jetzt hin?".
 *
 * WAS DIESER LESER KANN — UND WAS NICHT
 *
 *   Die Zeiten sind Freitext, von Hand oder aus dem Netz. Sechs echte Beispiele
 *   aus Martins Kontakten:
 *
 *     Mo-Do 06:00-16:30, Fr 06:00-15:00
 *     Mi – Sa: 11:00 – 21:00, So: 10:00 – 20:00, Mo + Di: Ruhetag
 *     Mo-Fr 8-12 Uhr, 13-17 Uhr
 *     Montag, Donnerstag, Freitag 11:30–14:00 & 17:00–20:30 Uhr
 *     nicht verfügbar
 *
 *   Er deckt diese Muster ab. Was er NICHT sicher versteht, beantwortet er mit
 *   null — dann zeigt die Karte einfach den Rohtext wie bisher. Ein falsches
 *   "jetzt offen" ist schlimmer als gar keine Auskunft: Danach steht Martin vor
 *   einer verschlossenen Tür.
 */

/** Montag = 1 … Sonntag = 7 (ISO). */
export type Wochentag = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface Zeitfenster {
  tag: Wochentag;
  vonMin: number; // Minuten seit Mitternacht
  bisMin: number;
}

const TAG_NAMEN: ReadonlyArray<[RegExp, Wochentag]> = [
  [/^mo(ntag)?$/i, 1],
  [/^di(enstag)?$/i, 2],
  [/^mi(ttwoch)?$/i, 3],
  [/^do(nnerstag)?$/i, 4],
  [/^fr(eitag)?$/i, 5],
  [/^sa(mstag)?$/i, 6],
  [/^so(nntag)?$/i, 7],
];

function tagAus(wort: string): Wochentag | null {
  // "So:" und "Mo." kommen beide vor — beides abstreifen, sonst passt kein
  // Muster. (Echte Daten: "Mi – Sa: 11:00 – 21:00, So: 10:00 – 20:00")
  const w = wort.trim().replace(/[.:]+$/, "");
  for (const [re, tag] of TAG_NAMEN) if (re.test(w)) return tag;
  return null;
}

/** "8" → 480, "07:30" → 450, "8.30" → 510. */
function minutenAus(text: string): number | null {
  const m = /^(\d{1,2})(?:[:.](\d{2}))?$/.exec(text.trim());
  if (!m?.[1]) return null;
  const std = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  if (std > 23 || min > 59) return null;
  return std * 60 + min;
}

/**
 * Die Tage aus einem Kopf wie "Mo-Do", "Mi – Sa", "Mo + Di" oder
 * "Montag, Donnerstag, Freitag".
 */
function tageAus(kopf: string): Wochentag[] {
  const roh = kopf.replace(/\bund\b/gi, "+").trim();
  const tage = new Set<Wochentag>();

  // Bereiche zuerst: "Mo-Do", "Mi – Sa"
  for (const m of roh.matchAll(/([A-Za-zÄÖÜäöü]+)\s*[–—-]\s*([A-Za-zÄÖÜäöü]+)/g)) {
    const a = tagAus(m[1] ?? "");
    const b = tagAus(m[2] ?? "");
    if (a && b) {
      let t = a;
      for (let i = 0; i < 7; i++) {
        tage.add(t);
        if (t === b) break;
        t = ((t % 7) + 1) as Wochentag;
      }
    }
  }
  // Einzelne: "Mo + Di", "Montag, Donnerstag"
  for (const wort of roh.split(/[,+/&]| und /i)) {
    const t = tagAus(wort.replace(/[–—-].*$/, ""));
    if (t) tage.add(t);
  }
  return [...tage].sort((a, b) => a - b);
}

/**
 * Freitext in Zeitfenster übersetzen. Leeres Ergebnis heißt: nicht verstanden.
 */
export function leseOeffnungszeiten(text: string | null | undefined): Zeitfenster[] {
  const roh = (text ?? "").trim();
  if (!roh) return [];
  if (/nicht verf(ü|ue)gbar|unbekannt|keine angabe/i.test(roh)) return [];

  const fenster: Zeitfenster[] = [];
  // In Abschnitte trennen: Komma oder Semikolon zwischen Tagesgruppen.
  // "Mo-Do 06:00-16:30, Fr 06:00-15:00" → zwei Abschnitte.
  let letzteTage: Wochentag[] = [];
  // Tage, die in einem eigenen Abschnitt OHNE Zeiten standen und auf den
  // naechsten Abschnitt mit Zeiten warten. Echter Fall: "Montag, Donnerstag,
  // Freitag 11:30–14:00" — Montag und Donnerstag haben keine eigene Spanne,
  // gelten aber offensichtlich fuer dieselbe. Ohne dieses Sammeln fielen sie
  // stillschweigend weg, und die Karte haette an zwei von drei Tagen
  // "geschlossen" behauptet.
  let wartendeTage: Wochentag[] = [];
  for (const teil of roh.split(/[;,]/)) {
    const stueck = teil.trim();
    if (!stueck) continue;
    if (/ruhetag|geschlossen/i.test(stueck)) {
      // "Mo + Di: Ruhetag" — diese Tage bekommen KEIN Fenster. Da wir nur
      // offene Zeiten sammeln, genuegt es, sie zu uebergehen.
      letzteTage = [];
      wartendeTage = [];
      continue;
    }
    // Kopf (Tage) vom Rest trennen — der Rest beginnt mit der ersten Ziffer.
    const ziffer = stueck.search(/\d/);
    const kopf = ziffer > 0 ? stueck.slice(0, ziffer) : "";
    const rest = ziffer >= 0 ? stueck.slice(ziffer) : "";
    if (!rest) {
      // Nur Tage, keine Zeiten — merken und auf den naechsten Abschnitt warten.
      wartendeTage = [...wartendeTage, ...tageAus(stueck)];
      continue;
    }

    const eigene = kopf.trim() ? tageAus(kopf) : [];
    const tage = eigene.length
      ? [...new Set([...wartendeTage, ...eigene])].sort((a, b) => a - b)
      : wartendeTage.length
        ? wartendeTage
        : letzteTage;
    wartendeTage = [];
    if (!tage.length) continue;
    letzteTage = tage;

    // Spannen im Rest: "06:00-16:30", "8-12", "11:30–14:00 & 17:00–20:30"
    for (const m of rest.matchAll(
      /(\d{1,2}(?:[:.]\d{2})?)\s*[–—-]\s*(\d{1,2}(?:[:.]\d{2})?)/g,
    )) {
      const von = minutenAus(m[1] ?? "");
      const bis = minutenAus(m[2] ?? "");
      if (von === null || bis === null || bis <= von) continue;
      for (const tag of tage) fenster.push({ tag, vonMin: von, bisMin: bis });
    }
  }
  return fenster;
}

type OffenStand =
  | { art: "offen"; bisMin: number }
  | { art: "gleich"; abMin: number } // öffnet heute noch
  | { art: "zu"; naechsterTag: Wochentag; abMin: number }
  | { art: "unbekannt" };

/** hh:mm für die Anzeige. */
function alsUhrzeit(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

const TAG_KURZ = ["", "Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

/** "Mo" für die Anzeige des nächsten offenen Tages. */
function tagKurz(tag: Wochentag): string {
  return TAG_KURZ[tag] ?? "";
}

/**
 * Ist gerade offen — und wenn nicht, wann wieder?
 *
 * `jetzt` wird übergeben, damit die Prüfung testbar bleibt.
 */
export function offenStand(
  fenster: Zeitfenster[],
  jetzt = new Date(),
): OffenStand {
  if (!fenster.length) return { art: "unbekannt" };
  const heute = (((jetzt.getDay() + 6) % 7) + 1) as Wochentag; // So=0 → 7
  const jetztMin = jetzt.getHours() * 60 + jetzt.getMinutes();

  const heuteFenster = fenster
    .filter((f) => f.tag === heute)
    .sort((a, b) => a.vonMin - b.vonMin);

  for (const f of heuteFenster) {
    if (jetztMin >= f.vonMin && jetztMin < f.bisMin) {
      return { art: "offen", bisMin: f.bisMin };
    }
  }
  const spaeterHeute = heuteFenster.find((f) => f.vonMin > jetztMin);
  if (spaeterHeute) return { art: "gleich", abMin: spaeterHeute.vonMin };

  // Nächster Tag mit einem Fenster — bis zu sieben Tage vorwärts.
  for (let i = 1; i <= 7; i++) {
    const tag = ((((heute - 1 + i) % 7) + 1) as Wochentag);
    const naechste = fenster
      .filter((f) => f.tag === tag)
      .sort((a, b) => a.vonMin - b.vonMin)[0];
    if (naechste) return { art: "zu", naechsterTag: tag, abMin: naechste.vonMin };
  }
  return { art: "unbekannt" };
}

/** Ein Satz für die Karte — oder null, wenn nichts Sicheres zu sagen ist. */
export function offenText(
  rohzeiten: string | null | undefined,
  jetzt = new Date(),
): string | null {
  const stand = offenStand(leseOeffnungszeiten(rohzeiten), jetzt);
  switch (stand.art) {
    case "offen":
      return `Jetzt offen · bis ${alsUhrzeit(stand.bisMin)}`;
    case "gleich":
      return `Öffnet um ${alsUhrzeit(stand.abMin)}`;
    case "zu":
      return `Geschlossen · ${tagKurz(stand.naechsterTag)} ab ${alsUhrzeit(stand.abMin)}`;
    default:
      return null;
  }
}
